// src/oura/newscan/fixtures/decisions.ts
//
// What needs a decision before the 50 can be approved, computed on every read
// from the rows, the lint and the draft, the way the backend computes them.
// Ids are `type:key`, so a dismissal or an ack survives the recomputation.
// Only `answered` (dismissed / acked) is stored.
//
// Worst first: blocker, decide, suggestion, info. A recipe change, while it is
// unanswered, is always the first card.
//
// Brand drafts (v4.5) add their own: a problem question that names the
// product, the brand's own words or a place in a question (blockers, each with
// the fix as the blue button), a selling point over 5 words (decide), rows no
// Google search supports and thin pulls (notes), and a buy answer the site
// read contradicts (mode_mismatch, both ways). The outlet, town and category
// cap decisions are never raised on a brand draft. A sample the local
// questions do not fit (an online shop) asks for Online instead of Both.

import type {
    BuyMode,
    DecisionOption,
    DecisionSeverity,
    ScanDraftAlternative,
    ScanDraftBusiness,
    ScanDraftDecision,
    ScanDraftDemand,
    ScanDraftKind,
} from "@service/aeo/scanDraftTypes";
import type { AssembledRow, DraftState, FirstCheckFacts, RecipeMode } from "./model";
import { firstCheckDecisions, planDecisions, presaleLine, type FirstCheckDecisionInput } from "./firstCheck";
import type { LintIssue } from "./lint";
import { tooSimilar } from "./lint";
import { CATEGORY_CAP, NEGATIVE_AFTER_GOOD_FOR, PLACE_VARS, SERVICE_SWAP_TARGETS, wordLabel } from "./recipe";
import { renderTemplate } from "./render";
import { DEMAND_THIN_BELOW, DIFFERENTIATOR_MAX_WORDS, containsPhrase, joinLabels, marketLabel, placeIn, sentenceCase, wordCount, withoutPlace } from "./brand";
import { rowCtx } from "./alternatives";
import { formatSearches, searchMatches } from "./search";
import { widerTown, type FixtureSample } from "./samples";
import type { WordSource } from "./words";

export interface DecisionInput {
    kind: ScanDraftKind;
    built: ScanDraftBusiness;
    business: ScanDraftBusiness;
    rows: AssembledRow[];
    issues: LintIssue[];
    src: WordSource;
    sample: FixtureSample | null;
    answered: Record<string, "dismissed" | "acked" | "resolved">;
    alternatives: (row: AssembledRow) => ScanDraftAlternative[];
    searchConnected: boolean;
    recipeMode: RecipeMode | null;
    /** Rows that differ from the approved set (edit / recipe change). */
    changed: number[];
    /** Rows the recipe change would reword. */
    recipeChanged: number[];
    /** edit_set / recipe_change: when the business's baseline was measured (ms). Null: none. */
    baselineAt?: number | null;
    /** Rows Oura reworded on generation because they repeated another question (dedupe.ts). */
    autoReworded?: AutoRewordedRow[];
    /** Brand drafts: the Google searches pull. */
    demand?: ScanDraftDemand;
    /**
     * A first_check draft (JOURNEY-SPEC §2.5): its facts, "For the plan"
     * answers and the count against the pitch. Absent on every other draft.
     */
    firstCheck?: {
        facts: FirstCheckFacts;
        forPlan: DraftState["forPlan"];
        changedVsPitch: number | null;
        nameTokens: string[];
        /** The pitch scan this draft copied is still running. */
        pitchRunning?: boolean;
    };
}

const isBrandInput = (input: DecisionInput) => input.built.buy === "online";

/** One row the dedupe pass reworded: its number, the generated wording and the one it has now. */
export interface AutoRewordedRow {
    n: number;
    from: string;
    to: string;
}

const SEVERITY_RANK: Record<DecisionSeverity, number> = { blocker: 0, decide: 1, suggestion: 2, info: 3 };

export const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

const slugOf = (s: string) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

function decision(
    type: ScanDraftDecision["type"],
    key: string,
    severity: DecisionSeverity,
    fields: Partial<Omit<ScanDraftDecision, "id" | "type" | "severity">> & { title: string },
): ScanDraftDecision {
    return {
        id: `${type}:${key}`,
        type,
        severity,
        detail: null,
        quotes: [],
        slots: [],
        state: "open",
        recommended: null,
        alternatives: [],
        evidence: null,
        ...fields,
    };
}

const editWord = (variable: string, value: string, label: string): DecisionOption => ({ label, action: { kind: "edit_word", variable, value } });
const editor = (n: number, label: string): DecisionOption => ({ label, action: { kind: "open_editor", n } });

/**
 * An option that puts in a whole new question: the wording goes in `preview`
 * (the card shows it in its own block), so the button stays short. Typed wider
 * than the contract's DecisionOption until it declares `preview`.
 */
type PreviewedOption = DecisionOption & { preview: string };
const rewordTo = (n: number, text: string): DecisionOption =>
    ({ label: "Use this wording", preview: text, action: { kind: "reword", n, text } }) as PreviewedOption;
const swapTo = (n: number, alt: { id: string; text: string }): DecisionOption =>
    ({ label: "Use this question", preview: alt.text, action: { kind: "swap_question", n, altId: alt.id } }) as PreviewedOption;

/* ------------------------------------------------------------------------- *
 * Row-driven: lint errors, blanks, duplicates, opposite meanings
 * ------------------------------------------------------------------------- */

function missingWord(row: AssembledRow, variable: string, input: DecisionInput): ScanDraftDecision {
    const n = row.question.n;
    const picks = PLACE_VARS.has(variable) ? [] : input.src.alternatives(variable);
    const uses = picks.map((p) => editWord(variable, p, `Use ${p}`));
    const typeOne: DecisionOption[] = PLACE_VARS.has(variable) ? [] : [{ label: "Type one", action: { kind: "open_word", variable } }];
    // A pick with no source is a guess: typing the real value leads, the picks follow.
    const source = PLACE_VARS.has(variable) ? null : input.sample?.words[variable]?.source ?? null;
    const ordered = source ? [...uses, ...typeOne] : [...typeOne, ...uses];
    return decision("missing_word", variable, "blocker", {
        title: variable === "price_point" ? "No published price on the site" : `Missing: ${wordLabel(variable).toLowerCase()}`,
        detail: `Question ${n} needs one, and the site shows none.`,
        // The blank stays a {word} token: the screen draws it as a blank.
        quotes: [row.question.text],
        slots: [n],
        recommended: ordered[0] ?? null,
        alternatives: ordered.slice(1),
        source,
    });
}

/** The row's wording still holds a `{word}` with no value. */
const hasBlank = (row: AssembledRow): boolean => /\{[a-z0-9_]+\}/.test(row.question.text);

/** A row staff changed: reworded, taken by "try another", or using a word staff edited. */
const staffChanged = (row: AssembledRow, input: DecisionInput): boolean =>
    row.question.reworded || row.question.swapped || row.question.fill_vars.some((v) => input.src.edited(v));

function duplicateOf(row: AssembledRow, other: AssembledRow, input: DecisionInput, severity: DecisionSeverity): ScanDraftDecision {
    const n = row.question.n;
    const options: DecisionOption[] = [];
    const rewriteTpl = input.sample?.rewrites[row.plan.key];
    let rewrite: DecisionOption | null = null;
    if (rewriteTpl) {
        const r = renderTemplate(rewriteTpl, rowCtx(row), (v) => input.src.value(v));
        const clash = input.rows.some((x) => x !== row && tooSimilar(x.question.text, r.text));
        if (!r.missing.length && !clash) rewrite = rewordTo(n, r.text);
    }
    const alt = input.alternatives(row).find((a) => a.kind === "template");
    const swap: DecisionOption | null = alt ? swapTo(n, alt) : null;
    const recommended = rewrite ?? swap;
    if (swap && swap !== recommended) options.push(swap);
    // On a staff-made repeat, "Edit myself" opens the row staff changed, not the generator's row.
    const target = staffChanged(row, input) || !staffChanged(other, input) ? n : other.question.n;
    options.push(editor(target, `Edit ${target} myself`));
    // A near repeat is staff's call: both may stay (an exact repeat must be fixed).
    if (severity !== "blocker") options.push({ label: "Keep both", action: { kind: "dismiss" } });
    // A word staff typed made the pair: say which, so a count that goes up after a fill is explained.
    const typed = [row, other]
        .flatMap((r) => r.question.fill_vars.filter((v) => input.src.edited(v)).map((v) => r.question.fill_values[v] ?? input.src.value(v)))
        .find((v) => Boolean(v && v.trim()));
    return decision("duplicate", row.plan.key, severity, {
        title: `Questions ${other.question.n} and ${n} ask the same thing`,
        detail: typed ? `They looked different while the word was blank. With “${typed}” in, they ask the same thing.` : null,
        quotes: [other.question.text, row.question.text],
        slots: [other.question.n, n],
        recommended,
        alternatives: options,
    });
}

/** One repeat the generator left: `row` repeats the earlier `other`. */
interface GeneratedRepeat {
    row: AssembledRow;
    other: AssembledRow;
    exact: boolean;
}

/**
 * Why the generator left a repeat: an outlet whose area is also its town (its
 * location rows and the city-level rows name the same place), else no
 * single cause. Returns the area as the rows write it, or null.
 */
function areaIsTownCause(p: GeneratedRepeat, rows: AssembledRow[]): string | null {
    const town = (r: AssembledRow) => (r.plan.town ?? "").trim().toLowerCase();
    const t = town(p.row);
    if (!t || t !== town(p.other)) return null;
    if (p.row.plan.check === "location" && p.other.plan.check === "location") return null;
    const outlet = rows.find((r) => r.plan.check === "location" && r.plan.place && r.plan.place.trim().toLowerCase() === t && town(r) === t);
    return outlet ? (outlet.plan.place as string) : null;
}

/**
 * The repeats the generator could not settle, one decision per cause. An
 * area that is also its town: "<k> questions repeat others because <Area> is
 * both the outlet and its town", fixed by asking the city-wide questions in
 * the wider town (fixtures WIDER_TOWN) when there is one. Any other cause with
 * one pair keeps the pair's own decision; with more, one "<k> questions
 * repeat others" decision. Staff-made repeats come here only when the town is the cause.
 */
function generatedRepeats(repeats: GeneratedRepeat[], input: DecisionInput): Array<{ at: GeneratedRepeat; d: ScanDraftDecision }> {
    const groups = new Map<string, { area: string | null; pairs: GeneratedRepeat[] }>();
    repeats.forEach((p) => {
        const area = areaIsTownCause(p, input.rows);
        const key = area ? `town-${slugOf(area)}` : "set";
        const g = groups.get(key) ?? { area, pairs: [] };
        g.pairs.push(p);
        groups.set(key, g);
    });
    const out: Array<{ at: GeneratedRepeat; d: ScanDraftDecision }> = [];
    groups.forEach((g, key) => {
        const first = g.pairs[0];
        const severity: DecisionSeverity = g.pairs.some((p) => p.exact) ? "blocker" : "decide";
        if (!g.area && g.pairs.length === 1) {
            out.push({ at: first, d: duplicateOf(first.row, first.other, input, severity) });
            return;
        }
        const repeating = Array.from(new Set(g.pairs.map((p) => p.row.question.n))).sort((x, y) => x - y);
        const slots = Array.from(new Set(g.pairs.flatMap((p) => [p.other.question.n, p.row.question.n]))).sort((x, y) => x - y);
        const k = repeating.length;
        const repeat = k === 1 ? "1 question repeats another" : `${k} questions repeat others`;
        // A blank word reads as "…" in a sentence (the quotes below draw it as a blank).
        const detail = g.pairs.map((p) => `${p.other.question.n} and ${p.row.question.n}: ${p.row.question.text.replace(/\{[a-z0-9_]+\}/g, "…")}`).join("\n");
        const quotes = g.pairs.map((p) => p.row.question.text);
        const edit = editor(repeating[0], k === 1 ? "Edit it myself" : "Edit them myself");
        if (g.area) {
            const area = g.area;
            const wider = widerTown(area);
            const outlet = input.built.outlets.find((o) => o.name.trim().toLowerCase() === area.trim().toLowerCase())?.name ?? area;
            out.push({
                at: first,
                d: decision("duplicate", key, severity, {
                    title: `${repeat} because ${area} is both the outlet and its town`,
                    detail,
                    quotes,
                    slots,
                    recommended: wider ? { label: `Use ${wider} for city-wide questions`, action: { kind: "set_town", outlet, town: wider } } : null,
                    alternatives: [edit],
                }),
            });
            return;
        }
        out.push({ at: first, d: decision("duplicate", key, severity, { title: repeat, detail, quotes, slots, recommended: null, alternatives: [edit] }) });
    });
    return out;
}

function oppositeMeaning(row: AssembledRow, phrase: string, input: DecisionInput): ScanDraftDecision {
    const n = row.question.n;
    const q = row.question;
    // The word that IS the phrase, else the longest word inside it ("a mattress that sags", not "mattress").
    const variable = q.fill_vars
        .filter((v) => !PLACE_VARS.has(v))
        .map((v) => ({ v, value: (q.fill_values[v] ?? "").toLowerCase() }))
        .filter((x) => x.value && phrase.includes(x.value))
        .sort((a, b) => b.value.length - a.value.length)[0]?.v;
    const detail = `Read as written, it asks for one that is good for “${phrase}”, not one that fixes it.`;
    // A reword keeps what the question asked (a mattress that won't sag); a word swap drops it, so it comes second.
    let reword: DecisionOption | null = null;
    const rewriteTpl = input.sample?.rewrites[row.plan.key];
    if (rewriteTpl) {
        const r = renderTemplate(rewriteTpl, rowCtx(row), (v) => input.src.value(v));
        const clash = input.rows.some((x) => x !== row && tooSimilar(x.question.text, r.text));
        if (!r.missing.length && !clash) reword = rewordTo(n, r.text);
    }
    if (!variable) {
        return decision("opposite_meaning", row.plan.key, "decide", {
            title: `Question ${n} asks for the opposite`,
            detail,
            quotes: [q.text],
            slots: [n],
            recommended: reword,
            alternatives: [editor(n, `Edit ${n} myself`)],
        });
    }
    const picks = input.src.alternatives(variable).filter((p) => !NEGATIVE_AFTER_GOOD_FOR.test(p.toLowerCase()));
    const uses = picks.map((p) => editWord(variable, p, `Use “${p}”`));
    const ordered = [...(reword ? [reword] : []), ...uses];
    return decision("opposite_meaning", variable, "decide", {
        title: `Question ${n} asks for the opposite`,
        detail,
        quotes: [q.text],
        slots: [n],
        recommended: ordered[0] ?? null,
        alternatives: [...ordered.slice(1), editor(n, `Edit ${n} myself`)],
    });
}

function lintError(row: AssembledRow, issue: LintIssue, input: DecisionInput): ScanDraftDecision {
    const n = row.question.n;
    const what = issue.message.replace(/^q\d+: /, "");
    if (row.question.reworded) {
        const back: DecisionOption[] = row.question.original_text
            ? [{ label: "Undo my edit", action: { kind: "reword", n, text: row.question.original_text } }]
            : [];
        return decision("lint_error", row.plan.key, "blocker", {
            title: `Question ${n}: ${what}`,
            quotes: [row.question.text],
            slots: [n],
            recommended: back[0] ?? null,
            alternatives: [editor(n, `Edit ${n} myself`)],
        });
    }
    const alt = input.alternatives(row)[0];
    return decision("lint_error", row.plan.key, "blocker", {
        title: `Question ${n}: ${what}`,
        quotes: [row.question.text],
        slots: [n],
        recommended: alt ? swapTo(n, alt) : null,
        alternatives: [editor(n, `Edit ${n} myself`)],
    });
}

/* ------------------------------------------------------------------------- *
 * Brand rows (v4.5)
 * ------------------------------------------------------------------------- */

/**
 * The wording a brand row's blocker recommends: the row as approved (when the
 * first draft wrote something else), else the Google search behind it in
 * sentence case, else `extra`. Never one that still trips `bad`, repeats the
 * current wording or comes too close to another question.
 */
function brandFix(row: AssembledRow, input: DecisionInput, bad: (text: string) => boolean, extra: string[] = []): string | null {
    const def = row.plan.brand;
    const canonical = def ? renderTemplate(def.question, {}, (v) => input.src.value(v)) : null;
    const candidates = [
        canonical && !canonical.missing.length ? canonical.text : "",
        ...extra,
        row.question.trace ? sentenceCase(row.question.trace.suggestion) : "",
    ].filter(Boolean);
    const others = input.rows.filter((r) => r !== row).map((r) => r.question.text);
    return (
        candidates.find(
            (t) => t.toLowerCase() !== row.question.text.toLowerCase() && !bad(t) && !others.some((o) => tooSimilar(o, t)) && !placeIn(t),
        ) ?? null
    );
}

function categoryInProblemRow(row: AssembledRow, issue: LintIssue, input: DecisionInput): ScanDraftDecision {
    const n = row.question.n;
    const words = input.sample?.brand?.categoryWords ?? [issue.phrase ?? ""];
    const fix = brandFix(row, input, (t) => words.some((w) => containsPhrase(t, w)));
    const another = input.alternatives(row).find((a) => a.id.startsWith("d:"));
    return decision("category_in_problem_row", row.plan.key, "blocker", {
        title: `Question ${n} names the product`,
        detail: "Problem questions ask what the buyer wants, without the product word.",
        quotes: [row.question.text],
        slots: [n],
        recommended: fix ? rewordTo(n, fix) : null,
        alternatives: [
            ...(another ? [{ label: "Try another search", action: { kind: "swap_question", n, altId: another.id } } as DecisionOption] : []),
            editor(n, `Edit ${n} myself`),
        ],
    });
}

function claimInQuestion(row: AssembledRow, issue: LintIssue, input: DecisionInput): ScanDraftDecision {
    const n = row.question.n;
    const phrase = issue.phrase ?? "";
    const claims = input.sample?.brand?.claimPhrases ?? [phrase];
    const fix = brandFix(row, input, (t) => [...claims, input.built.businessName].some((c) => c.trim() && containsPhrase(t, c)));
    const isName = [input.built.businessName, ...(input.built.otherNames ?? [])].some((x) => x.trim().toLowerCase() === phrase.toLowerCase());
    return decision("claim_in_question", row.plan.key, "blocker", {
        title: `Question ${n} uses your own words`,
        detail: `“${phrase}” is the brand's own ${isName ? "name" : "line"}. Questions use the buyer's words.`,
        quotes: [row.question.text],
        slots: [n],
        recommended: fix ? rewordTo(n, fix) : null,
        alternatives: [editor(n, `Edit ${n} myself`)],
    });
}

function placeInBrandRow(row: AssembledRow, issue: LintIssue, input: DecisionInput): ScanDraftDecision {
    const n = row.question.n;
    const hit = issue.place as NonNullable<LintIssue["place"]>;
    const what = hit.kind === "near_me" ? "says “near me”" : hit.kind === "building" ? "names a building" : "names a city";
    const stripped = withoutPlace(row.question.text, hit, row.question.market ?? row.question.fill);
    const fix = brandFix(row, input, (t) => Boolean(placeIn(t)), [stripped]);
    return decision("place_in_brand_row", row.plan.key, "blocker", {
        title: `Question ${n} ${what}`,
        detail: "These questions name a market, never a city or “near me”.",
        quotes: [row.question.text],
        slots: [n],
        recommended: fix ? rewordTo(n, fix) : null,
        alternatives: [editor(n, `Edit ${n} myself`)],
    });
}

function rowDecisions(input: DecisionInput): ScanDraftDecision[] {
    const out: ScanDraftDecision[] = [];
    const byId = new Map<string, ScanDraftDecision>();
    const add = (d: ScanDraftDecision) => {
        const had = byId.get(d.id);
        if (had) {
            d.slots.forEach((s) => {
                if (!had.slots.includes(s)) had.slots.push(s);
            });
            return;
        }
        byId.set(d.id, d);
        out.push(d);
    };
    const byN = new Map(input.rows.map((r) => [r.question.n, r]));
    // Repeats the generator left, with where their decision goes in the list.
    const repeats: GeneratedRepeat[] = [];
    const repeatAt = new Map<GeneratedRepeat, number>();
    input.rows.forEach((row) => {
        const issues = input.issues.filter((i) => i.key === row.plan.key);
        if (row.question.reworded) {
            const err = issues.find((i) => i.severity === "error");
            if (err) add(lintError(row, err, input));
            else {
                // An edit that lands close to another question: the user's call, not a block.
                const near = issues.find((i) => i.code === "duplicate" && i.other && byN.get(i.other));
                if (near) add(duplicateOf(row, byN.get(near.other as number) as AssembledRow, input, "decide"));
            }
            return;
        }
        let generic = false;
        issues.forEach((issue) => {
            if (issue.code === "unfilled" && issue.variable) add(missingWord(row, issue.variable, input));
            else if (issue.code === "duplicate" && issue.other && byN.get(issue.other)) {
                // A row with a blank word already has its missing_word; two blanks only look alike.
                // Unless the two read the same blank in the same words: they stay the same whatever word
                // fills it (an outlet named after its town), so the repeat is asked up front, not after the fill.
                const other = byN.get(issue.other) as AssembledRow;
                if ((hasBlank(row) || hasBlank(other)) && row.question.text !== other.question.text) return;
                // A "try another" pick, a locked row or a staff word edit on either side: staff made it, so it keeps its own decision.
                const staff = [row, other].some((r) => r.question.swapped || r.question.locked || r.question.fill_vars.some((v) => input.src.edited(v)));
                const p: GeneratedRepeat = { row, other, exact: issue.severity === "error" };
                // An outlet named after its own town repeats whatever the words: those pairs stay in the one
                // town card, whose single answer settles them all, even after staff fill a word.
                if (staff && !areaIsTownCause(p, input.rows)) add(duplicateOf(row, other, input, issue.severity === "error" ? "blocker" : "decide"));
                else {
                    repeats.push(p);
                    repeatAt.set(p, out.length);
                }
            }
            else if (issue.code === "opposite" && issue.phrase) add(oppositeMeaning(row, issue.phrase, input));
            else if (issue.code === "category_word") add(categoryInProblemRow(row, issue, input));
            else if (issue.code === "claim") add(claimInQuestion(row, issue, input));
            else if (issue.code === "place" && issue.place) add(placeInBrandRow(row, issue, input));
            else if (issue.code === "untraced") return;
            else if (issue.severity === "error" && !generic) {
                generic = true;
                add(lintError(row, issue, input));
            }
        });
    });
    // Each grouped decision takes the place of its first repeat, latest first so earlier places hold.
    generatedRepeats(repeats, input)
        .map(({ at, d }) => ({ i: repeatAt.get(at) as number, d }))
        .sort((x, y) => y.i - x.i)
        .forEach(({ i, d }) => {
            if (!byId.has(d.id)) {
                byId.set(d.id, d);
                out.splice(i, 0, d);
            }
        });
    return out;
}

/* ------------------------------------------------------------------------- *
 * Draft-driven
 * ------------------------------------------------------------------------- */

function serviceUnused(input: DecisionInput): ScanDraftDecision[] {
    if (isBrandInput(input)) {
        // Brand rows have no service words: a product staff added is "in" a question when its text names it.
        const texts = input.rows.map((r) => r.question.text.toLowerCase()).join(" | ");
        return input.built.services
            .filter((s) => s.source === "staff" && s.name.trim())
            .filter((s) => !texts.includes(s.name.trim().toLowerCase().replace(/(es|s)$/, "")))
            .map((s) =>
                decision("service_unused", slugOf(s.name.trim()), "decide", {
                    title: `${s.name.trim()} is not in any question`,
                    detail: "You added it, and no Google search the 50 come from names it.",
                    recommended: null,
                    alternatives: [{ label: "Leave it out", action: { kind: "dismiss" } }],
                }),
            );
    }
    const used = new Set<string>();
    const values: string[] = [];
    input.rows.forEach(({ question }) => {
        question.fill_vars.forEach((v) => {
            if (PLACE_VARS.has(v)) return;
            used.add(v);
            values.push((question.fill_values[v] ?? "").toLowerCase());
        });
    });
    const joined = values.join(" | ");
    const target = SERVICE_SWAP_TARGETS.find((v) => used.has(v));
    return input.built.services
        .filter((s) => s.source === "staff" && s.name.trim())
        .filter((s) => !joined.includes(s.name.trim().toLowerCase().replace(/(es|s)$/, "")))
        .map((s) => {
            const name = s.name.trim();
            const uses = target ? input.rows.filter((r) => !r.question.locked && r.question.fill_vars.includes(target)).length : 0;
            return decision("service_unused", slugOf(name), "decide", {
                title: `${name} is in no question`,
                detail: "You added it, but the draft used other services.",
                recommended: target
                    ? // Keep the case staff typed: "Pilates rehab" reads in the question as they wrote it.
                      editWord(target, name, `Use it instead of ${input.src.value(target)} (${plural(uses, "question")})`)
                    : null,
                alternatives: [{ label: "Leave it out", action: { kind: "dismiss" } }],
            });
        });
}

function outletUnverified(input: DecisionInput): ScanDraftDecision[] {
    if (input.business.buy === "online") return [];
    return input.business.outlets
        .filter((o) => o.source === "staff" && o.confirmed !== true && o.name.trim())
        .map((o) =>
            decision("outlet_unverified", slugOf(o.name), "decide", {
                title: `${o.name} is not on the website`,
                detail: "You added it, and the site never names it. Keep it if it is open: it gets its own questions. Leave it out and no question asks about it.",
                recommended: { label: "It is open, keep it", action: { kind: "confirm_outlet", name: o.name } },
                alternatives: [{ label: "Leave it out", action: { kind: "drop_outlet", name: o.name } }],
            }),
        );
}

function recipeChange(input: DecisionInput): ScanDraftDecision[] {
    if (input.kind !== "recipe_change" || input.recipeMode !== null) return [];
    const k = input.recipeChanged.length;
    const profession = input.src.value("category") || "the profession";
    const flagship = input.built.outlets[0]?.name ?? input.built.city;
    return [
        decision("recipe_change", "recipe", "decide", {
            title: `The new recipe changes ${k} of these 50`,
            detail: `The old recipe asked for the profession (“Best ${profession} in ${flagship}?”). The new one asks the problem. Taking a change restarts that question's trend.${baselineLine(input)}`,
            slots: input.recipeChanged,
            recommended: { label: `Take all ${k}`, action: { kind: "recipe", mode: "take_all" } },
            alternatives: [
                { label: "Choose each", action: { kind: "choose_each" } },
                { label: "Keep all", action: { kind: "recipe", mode: "keep_all" } },
            ],
        }),
    ];
}

/** The plan-aware sentence (JOURNEY-SPEC §2.7) on a business with a baseline. */
function baselineLine(input: DecisionInput): string {
    if (!input.baselineAt) return "";
    const day = new Date(input.baselineAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    return ` The baseline stays on ${day}. Monthly reports compare only the questions both sets ask.`;
}

function trendBreak(input: DecisionInput): ScanDraftDecision[] {
    if (input.kind === "new_scan" || !input.changed.length) return [];
    if (input.kind === "recipe_change" && input.recipeMode === null) return [];
    const k = input.changed.length;
    return [
        decision("trend_break", "set", "decide", {
            title: `${plural(k, "question")} change${k === 1 ? "s" : ""}, so ${k === 1 ? "its" : "their"} trend restarts`,
            detail: `Results so far stay in Question history. From this scan on, a changed question starts a new trend line.${baselineLine(input)}`,
            slots: input.changed,
            recommended: { label: "I understand", action: { kind: "ack" } },
        }),
    ];
}

function searchCandidates(input: DecisionInput): ScanDraftDecision[] {
    // Brand rows take no word swaps: a search no question asks is for the next pull, not a word edit.
    if (!input.searchConnected || input.kind === "recipe_change" || !input.sample || isBrandInput(input)) return [];
    return input.sample.searches
        .filter((s) => s.candidate)
        .filter((s) => !input.rows.some((r) => searchMatches(r.question.fill_values, s)))
        .filter((s) => s.candidate && input.src.value(s.candidate.variable).toLowerCase() !== s.candidate.value.toLowerCase())
        .map((s) => {
            const { variable, value } = s.candidate as { variable: string; value: string };
            const uses = input.rows.filter((r) => !r.question.locked && r.question.fill_vars.includes(variable)).length;
            const current = input.src.value(variable);
            return decision("gsc_candidate", slugOf(s.query), "suggestion", {
                title: `People search “${s.query}” ${formatSearches(s.searches)} times a year. No question asks it.`,
                recommended: editWord(variable, value, uses && current ? `Swap it in for ${current} (${plural(uses, "question")})` : `Use “${value}”`),
                alternatives: [{ label: "Ignore", action: { kind: "dismiss" } }],
                evidence: { query: s.query, searches: s.searches },
            });
        });
}

/**
 * The repeats Oura settled itself, as one quiet note: no button, nothing to
 * approve. Staff who want another wording edit the row like any other.
 * The detail is one line per row, "17. <new wording> (was: <old wording>)".
 */
function autoReworded(input: DecisionInput): ScanDraftDecision[] {
    const rows = input.autoReworded ?? [];
    if (!rows.length) return [];
    const k = rows.length;
    return [
        decision("auto_reworded", "set", "info", {
            title: k === 1 ? "Oura reworded 1 question that repeated another" : `Oura reworded ${k} questions that repeated others`,
            detail: rows.map((r) => `${r.n}. ${r.to} (was: ${r.from})`).join("\n"),
            quotes: rows.map((r) => r.to),
            slots: rows.map((r) => r.n),
        }),
    ];
}

/* ------------------------------------------------------------------------- *
 * Brand: the selling point, the searches, the mode
 * ------------------------------------------------------------------------- */

function differentiatorTooLong(input: DecisionInput): ScanDraftDecision[] {
    const issue = input.issues.find((i) => i.code === "differentiator_long");
    if (!issue) return [];
    const value = input.src.value("differentiator");
    const claims = input.sample?.brand?.claimPhrases ?? [];
    const pick = input.src
        .alternatives("differentiator")
        .find((p) => wordCount(p) <= DIFFERENTIATOR_MAX_WORDS && !claims.some((c) => containsPhrase(p, c)));
    const slots = input.rows.filter((r) => r.question.fill_vars.includes("differentiator")).map((r) => r.question.n);
    // No question uses the selling point (sells only to shoppers): nothing to fix, and "Type one" would land nowhere.
    if (slots.length === 0) return [];
    return [
        decision("differentiator_too_long", "differentiator", "decide", {
            title: `The selling point is ${wordCount(value)} words`,
            detail: `Keep it to ${DIFFERENTIATOR_MAX_WORDS} words and say what it is, not how good it is.`,
            quotes: [value],
            slots,
            recommended: pick ? editWord("differentiator", pick, `Use “${pick}”`) : { label: "Type one", action: { kind: "open_word", variable: "differentiator" } },
            alternatives: [
                ...(pick ? [{ label: "Type one", action: { kind: "open_word", variable: "differentiator" } } as DecisionOption] : []),
                { label: "Leave it", action: { kind: "dismiss" } },
            ],
        }),
    ];
}

/** "Questions 14, 27 and 41" / "Question 14". */
const questionsList = (ns: number[]) => (ns.length === 1 ? `Question ${ns[0]}` : `Questions ${joinLabels(ns.map(String))}`);

function untracedRows(input: DecisionInput): ScanDraftDecision[] {
    const slots = input.rows.filter((r) => r.question.trace === null).map((r) => r.question.n);
    if (!slots.length) return [];
    const k = slots.length;
    return [
        decision("untraced_row", "set", "info", {
            title: k === 1 ? "1 question has no Google search behind it" : `${k} questions have no Google search behind them`,
            detail: `${questionsList(slots)}. See ${k === 1 ? "it" : "them"} in Searches.`,
            quotes: input.rows.filter((r) => r.question.trace === null).map((r) => r.question.text),
            slots,
        }),
    ];
}

function demandThin(input: DecisionInput): ScanDraftDecision[] {
    const demand = input.demand;
    if (!demand?.items) return [];
    const items = demand.items;
    return demand.markets
        .map((code) => ({ code, found: items.filter((x) => x.market === code).length }))
        .filter((m) => m.found < DEMAND_THIN_BELOW)
        .map(({ code, found }) => {
            const k = input.rows.filter((r) => r.question.market === code).length;
            const asks = k === 0 ? "No question asks for it." : k === 1 ? "Its 1 question uses the closest one." : `Its ${k} questions use the closest ones.`;
            return decision("demand_thin", code, "info", {
                title: `${found ? "Few" : "No"} Google searches for ${marketLabel(code)}`,
                detail: `${found} found. ${asks}`,
                slots: input.rows.filter((r) => r.question.market === code).map((r) => r.question.n),
            });
        });
}

const BUY_LABEL: Record<BuyMode, string> = { visit: "Visit us", online: "Online", both: "Both", we_go_to_them: "We go to them" };

/**
 * The site read and the buy answer disagree (SPEC-v45 §5.1). Online with an
 * address customers visit: a door is found by place, so Both. Visit us or Both
 * where the read found no address and a cart, and staff confirmed none: Online.
 *
 * A sample whose local questions do not fit (an online mattress shop: they ask
 * for a treatment or a class near an outlet) never lands on them: Both, or a
 * local answer with a door staff confirmed, asks for Online instead, and
 * Online with a showroom stays Online (Use Both would only send it back).
 */
function modeMismatch(input: DecisionInput): ScanDraftDecision[] {
    if (input.kind !== "new_scan") return [];
    const b = input.business;
    const localFits = input.sample?.localFits !== false;
    if (b.buy === "online") {
        const door = b.outlets.find((o) => o.name.trim());
        if (!door || !localFits) return [];
        return [
            decision("mode_mismatch", "buy", "decide", {
                title: `${door.source === "site" ? "The site lists" : "You added"} a showroom in ${door.name.trim()}`,
                detail: "A business with a door is found by place. Both asks the showroom questions, and its products sit in the other questions.",
                recommended: { label: "Use Both", action: { kind: "set_buy", buy: "both" } },
                alternatives: [{ label: "Keep Online: no one visits it", action: { kind: "dismiss" } }],
            }),
        ];
    }
    const noAddress = noAddressOnSite(input);
    if (!localFits && (b.buy === "both" || !noAddress.length)) {
        return [
            decision("mode_mismatch", "buy", "decide", {
                title: "The local questions are written for clinics, studios and gyms",
                detail: "They ask for a treatment or a class near an outlet. Online asks what buyers search for, market by market.",
                recommended: { label: "Use Online instead", action: { kind: "set_buy", buy: "online" } },
            }),
        ];
    }
    return noAddress;
}

/** Visit us or Both where the read found no address and a cart, and staff confirmed none: Online. */
function noAddressOnSite(input: DecisionInput): ScanDraftDecision[] {
    const b = input.business;
    const read = input.sample?.detected;
    if (!read || read.buyGuess !== "online" || read.outlets.length) return [];
    if (b.outlets.some((o) => o.source === "staff" && o.confirmed === true)) return [];
    return [
        decision("mode_mismatch", "buy", "decide", {
            title: "No address customers visit on the site",
            detail: "Online asks what buyers search for, market by market, instead of the outlet questions.",
            recommended: { label: "Use Online", action: { kind: "set_buy", buy: "online" } },
            alternatives: [{ label: `Keep ${BUY_LABEL[b.buy]}: customers visit`, action: { kind: "dismiss" } }],
        }),
    ];
}

function shapeErrors(input: DecisionInput): ScanDraftDecision[] {
    const errors = input.issues.filter((i) => i.code === "shape");
    if (!errors.length) return [];
    return [
        decision("lint_error_set", "shape", "blocker", {
            title: "The 50 are not in the recipe's shape",
            detail: errors.map((e) => e.message).join("\n"),
            alternatives: [{ label: "Draft the 50 again", action: { kind: "rebuild" } }],
        }),
    ];
}

function brandNotes(input: DecisionInput): ScanDraftDecision[] {
    if (!isBrandInput(input)) return [];
    return [...shapeErrors(input), ...differentiatorTooLong(input), ...untracedRows(input), ...demandThin(input)];
}

function notes(input: DecisionInput): ScanDraftDecision[] {
    const out: ScanDraftDecision[] = [...autoReworded(input)];
    // Brand drafts: the category cap is replaced by category_in_problem_row, and there are no towns.
    if (isBrandInput(input)) return out;
    const cap = input.issues.find((i) => i.code === "category_cap");
    if (cap) {
        const used = Number(/(\d+) questions/.exec(cap.message)?.[1] ?? 0);
        out.push(
            decision("category_cap", "category", "info", {
                title: `The profession word is in ${used} questions`,
                detail: `The recipe keeps it to ${CATEGORY_CAP.warnAbove} or fewer, so the questions ask the problem, not the profession.`,
            }),
        );
    }
    const cityRows = input.rows.filter((r) => r.plan.check !== "location" && r.plan.town);
    const perTown: Record<string, number> = {};
    cityRows.forEach((r) => {
        const town = r.plan.town as string;
        perTown[town] = (perTown[town] ?? 0) + 1;
    });
    const towns = Array.from(new Set(input.rows.filter((r) => r.plan.check === "location" && r.plan.town).map((r) => r.plan.town as string)));
    if (towns.length > 1) {
        towns
            .filter((t) => (perTown[t] ?? 0) < 2)
            .forEach((t) =>
                out.push(
                    decision("towns_thin", slugOf(t), "info", {
                        title: `${t} carries only ${plural(perTown[t] ?? 0, "city-level question")}`,
                        detail: "Towns with an outlet get at least two city-level questions where the split allows.",
                    }),
                ),
            );
    }
    return out;
}

/** Every decision on the draft, worst first, with its answered state. */
export function computeDecisions(input: DecisionInput): ScanDraftDecision[] {
    // first_check: the form cards after the question decisions, "For the plan" after every other
    // decide, the pitch line last; never a trend break (the client has no trend yet).
    const fc: FirstCheckDecisionInput | null = input.firstCheck
        ? {
              facts: input.firstCheck.facts,
              nameTokens: input.firstCheck.nameTokens,
              business: input.business,
              rows: input.rows,
              src: input.src,
              forPlan: input.firstCheck.forPlan,
              changedVsPitch: input.firstCheck.changedVsPitch,
              leftOut: input.firstCheck.facts.formServices.filter((name) => input.answered[`form_service_new:${slugOf(name)}`] === "dismissed"),
              issues: input.issues,
              answered: input.answered,
              pitchRunning: input.firstCheck.pitchRunning,
          }
        : null;
    const all = [
        ...recipeChange(input),
        ...rowDecisions(input),
        ...(fc ? firstCheckDecisions(fc) : []),
        ...serviceUnused(input),
        ...outletUnverified(input),
        ...(fc ? [] : trendBreak(input)),
        ...searchCandidates(input),
        ...modeMismatch(input),
        ...brandNotes(input),
        ...(fc ? planDecisions(fc) : []),
        ...notes(input),
        ...(fc ? [presaleLine(fc.facts, fc.changedVsPitch, input.rows.length)] : []),
    ];
    all.forEach((d) => {
        const answered = input.answered[d.id];
        // A blocker can never be dismissed, even one a near-duplicate dismissal named before it became exact.
        if (d.severity === "blocker") return;
        if (answered === "dismissed") d.state = "dismissed";
        if (answered === "acked") d.state = "acked";
        if (answered === "resolved") d.state = "resolved";
    });
    const rank = (d: ScanDraftDecision) => (d.type === "recipe_change" ? -1 : SEVERITY_RANK[d.severity]);
    return all
        .map((d, i) => ({ d, i }))
        .sort((a, b) => rank(a.d) - rank(b.d) || a.i - b.i)
        .map(({ d }) => d);
}

/** Open decisions that hold Approve back. */
export function openGates(decisions: ScanDraftDecision[]): ScanDraftDecision[] {
    return decisions.filter((d) => d.state === "open" && (d.severity === "blocker" || d.severity === "decide"));
}
